import { useEffect, useMemo, useState } from "react";

export function usePagination<T>(
  items: T[],
  pageSize: number,
  /** Change this (e.g. filter string) to jump back to page 1. */
  resetKey?: string | number | boolean
): {
  page: number;
  setPage: (p: number) => void;
  pageCount: number;
  pageSize: number;
  total: number;
  slice: T[];
} {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), pageCount);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const slice = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  return {
    page: safePage,
    setPage,
    pageCount,
    pageSize,
    total,
    slice,
  };
}

export function PaginationBar({
  page,
  pageCount,
  total,
  pageSize,
  onPageChange,
  className,
}: {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className={`pagination-bar${className ? ` ${className}` : ""}`}>
      <span className="pagination-meta muted tiny">
        {from}–{to} of {total}
      </span>
      <div className="pagination-controls">
        <button
          type="button"
          className="btn ghost compact"
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
          aria-label="First page"
        >
          «
        </button>
        <button
          type="button"
          className="btn ghost compact"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span className="pagination-page muted tiny">
          Page {page} / {pageCount}
        </span>
        <button
          type="button"
          className="btn ghost compact"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          ›
        </button>
        <button
          type="button"
          className="btn ghost compact"
          disabled={page >= pageCount}
          onClick={() => onPageChange(pageCount)}
          aria-label="Last page"
        >
          »
        </button>
      </div>
    </div>
  );
}
